<template>
  <div>
    <div class="form-row">
      <div class="col-12 col-xl-6">
        <div class="form__input">
          <input-select
            id="documentType"
            v-model="documentType"
            name="documentType"
            label="Document Type"
            placeholder="What are you uploading today?"
            placeholder-enabled
            :options="[
              { id: 'license', display_label: 'Counseling License' },
              { id: 'governmentId', display_label: 'Government Photo Id' },
              { id: 'insurance', display_label: 'Malpractice Insurance' },
            ]"
          />
        </div>
      </div>
      <div class="col-12 col-xl-6" v-if="documentType === 'license'">
        <div class="form__input">
          <input-select
            id="usState"
            v-model="usState"
            name="usState"
            label="State"
            displayField="name"
            placeholder="Which US State does this license apply to?"
            placeholder-enabled
            :options="usStates"
          />
        </div>
      </div>
      <div class="col-12 col-xl-6" v-if="documentType === 'license'">
        <div class="form__input">
          <input-datepicker 
            id="expires"
            v-model="expires"
            name="expires"
            label="License Expires"
            placeholder="When does this license expire?"
            placeholder-enabled
          />
        </div>
      </div>
      <div class="col-12 col-xl-6" v-if="documentType === 'license'">
        <div class="form__input">
          <input-text 
            id="licenseNumber"
            v-model="licenseNumber"
            name="licenseNumber"
            label="License Number"
          />
        </div>
      </div>
      <div class="col-12 col-xl-6" v-if="documentType === 'license'">
        <div class="form__input">
          <input-select
            id="licenseType"
            v-model="licenseType"
            name="licenseType"
            label="License Type"
            placeholder="Which class of license are you uploading?"
            placeholder-enabled
            :options="[           
              { id: 'CreativeArtsTherapy', display_label: 'Creative Arts Therapy' },
              { id: 'LicensedClinicalProfessionalCounselor', display_label: 'LCPC - Licensed Clinical Professional Counselor' },
              { id: 'LicensedClinicalSocialWorker', display_label: 'LCSW - Licensed Clinical Social Worker' },
              { id: 'LicensedIndependentClinicalSocialWorker', display_label: 'LICSW - Licensed Independent Clinical Social Worker' },
              { id: 'LicensedIndependentSocialWorker', display_label: 'LISW - Licensed Independent Social Worker' },
              { id: 'LicensedMarriageAndFamilyTherapist', display_label: 'LMFT - Licensed Marriage And Family Therapist' },
              { id: 'LicensedMasterSocialWorker', display_label: 'LMSW - Licensed Master Social Worker' },
              { id: 'LicensedMentalHealthCounselor', display_label: 'LMHC - Licensed Mental Health Counselor' },
              { id: 'LicensedMentalHealthPractitioner', display_label: 'LMHP - Licensed Mental Health Practitioner' },
              { id: 'LicensedProfessionalClinicalCounselorOfMentalHealth', display_label: 'LPCC - Licensed Professional Clinical Counselor of Mental Health' },
              { id: 'LicensedProfessionalCounselor', display_label: 'LPC  - Licensed Professional Counselor' },
              { id: 'PsychiatricNursePractitioner', display_label: 'Psychiatric Nurse Practitioner' },
              { id: 'Psychiatrists', display_label: 'Psychiatrists' },
              { id: 'Psychologists', display_label: 'Psychologists' },
              { id: 'Unknown', display_label: 'Other' },
            ]"
          />
        </div>
      </div>
      <div class="col-12 col-xl-6"  v-if="documentType">
        <div class="form__input">
          <label class="form__label">
            License File
          </label>
          <div class="custom-file-container">
            <b-form-file
              id="fileUpload"
              v-model="fileUpload"
              name="fileUpload"
              accept="image/*,application/pdf,application/msword"
              placeholder="Select an image, PDF, DOC, or DOCX file"
              drop-placeholder="Drop file here..."
              :state="Boolean(fileUpload)"
            />
            <font-awesome-icon
              :icon="['fa', 'file-upload']"
              class="icon alt"
            />
          </div>
        </div>
      </div>
      <div class="col-12" v-if="documentType">
        <button
          type="button"
          :disabled="saving || !isValid"
          class="btn btn-primary btn-wide mt-4"
          @click="upload"
        >
          <font-awesome-icon
            v-if="saving"
            :spin="true"
            :icon="['fas', 'spinner']"
            class="icon alt"
          />
          <span v-else>Upload</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import UserClient from '@/api/UserClient.js'
import InputSelect from '@/components/inputs/InputSelect.vue'
import InputText from '@/components/inputs/InputText.vue'
import InputDatepicker from '@/components/inputs/InputDatepicker.vue'

export default {
  components:{
    InputSelect,
    InputDatepicker,
    InputText,
  },
  data(){
    return {
      userClient: new UserClient(this.$http),

      documentType: null,
      usState: null,
      licenseType: null,
      licenseNumber: null,
      expires: null,
      fileUpload: null,

      saving: false,
      usStates: [],
    }
  },
  inject:[
    'documents'
  ],
  async mounted(){
      const states  = await this.userClient.getStatesList('', 500);
      this.usStates = states.results || []
  },
  computed: {
    isValid(){
      if(this.documentType === 'license')
        return !!this.usState && !!this.licenseType && !!this.expires && !!this.fileUpload && !!this.licenseNumber;
      if (this.documentType === 'governmentId')
        return !!this.fileUpload;
      if (this.documentType === 'insurance')
        return !!this.fileUpload;
      
      return false;
    }
  },
  methods:{
    async upload() {
      try {
        if(!this.isValid || this.saving)
            return;
        this.saving = true;

        if(this.documentType === 'license')
          await this.documents.addLicense(
            this.licenseType, 
            this.usState, 
            this.licenseNumber,
            this.expires,
            this.fileUpload);

        else if (this.documentType === 'governmentId')
          await this.documents.setGovernmentId(this.fileUpload);

        else if (this.documentType === 'insurance')
          await this.documents.setInsurance(this.fileUpload);

        this.$store.commit('setGlobalAlert', {
          msg: 'Document Uploaded!'
        });
      } catch(ex) {
        console.error(ex);
        this.$store.commit('setGlobalAlert', {
          msg: 'Failed uploading document. Please try again!',
          error: true
        });
      } finally {
        this.reset();
      }
    },
    reset() {
      this.documentType = null;
      this.usState = null;
      this.licenseNumber = null;
      this.licenseType = null;
      this.expires = null;
      this.fileUpload = null;
      this.saving = false;
    }
  }
}
</script>

<style>

</style>